// src/components/crm/QuoteActivityTimeline.js
// SEAM 3 VERIFIER: qact-3b7e9d52-timeline
//
// Minimal, read-only activity timeline for one quote — a collapsible strip on
// the operator quote detail page, newest first. The rich version arrives with
// the pipeline board (Seam 8).
//
// ⚠️ CHANGED IN SEAM 6 (qfup-7d3e9a41). This component no longer owns its data
// or its row markup; both were EXTRACTED, not rewritten:
//   * the subscription  → src/hooks/useQuoteActivity.js
//   * describe() + the row → src/components/crm/QuoteActivityRow.js
// It now takes `entries` / `loadError` as props from QuoteDetailsView, which
// calls the hook ONCE and shares it with the follow-up strip. Passing neither
// is still supported: the component falls back to subscribing itself, so any
// future mount site keeps working unchanged. Rendering is byte-identical to
// Seam 3 — same labels, same layout, same note-deleted handling.
//
// Data: organizations/{orgId}/quotes/{quoteId}/activity, written ONLY by Cloud
// Functions (functions/utils/quoteActivity.js). Portal customers cannot read
// it (firestore.rules), so this component must never be mounted in a portal
// view.
//
// note_added rows: note CONTENT lives in Notes & Media, so a row only says a
// note was added and, when clicked, opens the Notes & Media strip on the same
// page. If the entry's noteId is not in the page's already-loaded notes list
// (quoteNotes — zero extra reads), the row reads "Note (deleted)" with no
// link: that covers a note-delete cleanup that failed server-side. Judged only
// once notes have finished loading, so rows don't flash "deleted".

import React, { useState } from 'react';
import { History, ChevronDown, ChevronUp } from 'lucide-react';
import { resolveDocTerminology } from '../../utils/quoteSchema';
import useQuoteActivity, { MAX_ROWS } from '../../hooks/useQuoteActivity';
import QuoteActivityRow, { pairCloseRows } from './QuoteActivityRow';

const QuoteActivityTimeline = ({
    orgId,
    quote,
    quoteNotes = [],
    notesLoading = true,
    onOpenNotes,
    pipelineStages = null,
    entries: entriesProp = null,
    loadError: loadErrorProp = null,
    closeRecord = null,   // qclose-3e8a5c17 — the quoteCloses record, for the answers line
}) => {
    const [expanded, setExpanded] = useState(false);
    const quoteId = quote?.id || null;
    const { word: docWord } = resolveDocTerminology(quote?.docType);

    // Fallback subscription for any mount site that doesn't supply the shared
    // one. Skipped (null ids) when entries come in as a prop, so the shared
    // path opens exactly one listener.
    const shared = entriesProp !== null;
    const own = useQuoteActivity(shared ? null : orgId, shared ? null : quoteId);
    const entries = shared ? entriesProp : own.entries;
    const loadError = shared ? !!loadErrorProp : own.loadError;

    // Hidden where the page has no orgId (e.g. the customer modal's Quotes tab —
    // a known gap logged in BACKLOG).
    if (!orgId || !quoteId) return null;

    const noteIds = new Set(quoteNotes.map((n) => n.id));
    // qclose-3e8a5c17 — one row per close: hide the stage_change each 'closed'
    // entry was written with. The count above still counts stored entries.
    const hidden = pairCloseRows(entries);
    const countLabel = entries.length >= MAX_ROWS ? `${MAX_ROWS}+` : String(entries.length);

    return (
        <div className="max-w-4xl mx-auto mb-4">
            <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50"
            >
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <History size={16} /> Activity
                    <span className="text-xs font-normal text-gray-400">({countLabel})</span>
                </span>
                {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
            </button>
            {expanded && (
                <div className="mt-2 bg-white rounded-lg shadow-sm border border-gray-100 p-4">
                    {loadError ? (
                        <p className="text-sm text-red-600 text-center py-4">Couldn't load activity. Try reopening this {docWord.toLowerCase()}.</p>
                    ) : entries.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No activity recorded yet. Sends and notes appear here from now on.</p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {entries.filter((entry) => !hidden.has(entry.id)).map((entry) => (
                                <QuoteActivityRow
                                    key={entry.id}
                                    entry={entry}
                                    docWord={docWord}
                                    stages={pipelineStages}
                                    noteIds={noteIds}
                                    notesLoading={notesLoading}
                                    onOpenNotes={onOpenNotes}
                                    closeRecord={closeRecord}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default QuoteActivityTimeline;
